import { computed } from "vue";
import offersHelper from "@/utils/helpers/offers";
import demandHelper from "@/utils/helpers/demand";
import MinarmRights from "@/components/details/MinarmRights.vue";
import { i18n } from "@/plugins/i18n";
import { createMoveManagerContext, type MoveManagerContext, type MoveManagerProps } from "./moveManagerBase";

export function useMoveManagerOfferPart(props?: MoveManagerProps, ctx?: MoveManagerContext) {
	const { t } = i18n.global;
	const context = ctx ?? createMoveManagerContext(props);
	const { currentOffer, demand, projectSelected, offers } = context;

	const isMinarm = computed(() => projectSelected.value?.params?.is_minarm);
	const isCommissionaire = computed(() => projectSelected.value?.params?.is_commissionaire);
	const isIndividual = computed(() => projectSelected.value?.params?.is_individual);
	const isValidationDoneByPartner = computed(() => projectSelected.value?.params?.partner_validates_quotes);
	const validOffer = computed(() => offersHelper.getValidOffer(offers.value, isIndividual.value || !isValidationDoneByPartner.value));
	const isCBPaymentEnabled = computed(() => projectSelected.value && !projectSelected.value.params.is_cb_payment_disabled);
	const disabledOffers = computed(() => demand.value?.is_booking_disabled);

	const leftToDoInfos = computed(() => ({
		icon: false,
		subtitle: i18n.global.t('resume.leftToDo.subtitle'),
		iconItem: ['fal', 'times-circle'],
	}));
	// La formule affichée reflète le choix final du client : si une option de type
	// formule est sélectionnée, son descriptif prime sur la formule de la demande.
	const chosenFormuleOption = computed(() => currentOffer.value?.options?.find(
		(option) => option.category === 'formule' && option.is_chosen,
	));
	const selectedFormule = computed(() => chosenFormuleOption.value?.formule ?? demand.value?.formule);
	const formuleInfos = computed(() => ({
		title: selectedFormule.value?.title ?? '',
		subtitle: t('resume.formule.subtitle'),
	}));

	const isFinished = computed(() => currentOffer.value && offersHelper.isFinished(offers.value));
	const isFinishedStatus = computed(() => currentOffer.value && offersHelper.isFinishedStatus(currentOffer.value));
	const isSigned = computed(() => currentOffer.value && offersHelper.isSigned(currentOffer.value));
	const isValidForPcd = computed(() => {
		if (isIndividual.value || !isValidationDoneByPartner.value) {
			return currentOffer.value && offersHelper.isPrebooked(currentOffer.value);
		}
		return currentOffer.value && offersHelper.isValidForPcd(currentOffer.value);
	});
	const isBookingCharge = computed(() => isCommissionaire.value && ((isFinishedStatus.value
		&& (isSigned.value || isFinished.value)) || isValidForPcd.value));

	const servicesInfos = computed(() => ({
		icon: ['fal', 'check-circle'],
		title: t('resume.services.title'),
		subtitle: t('resume.services.subtitle'),
	}));
	const chosenOptionsInfos = computed(() => ({
		icon: ['fal', 'plus-circle'],
		title: t('resume.options.title'),
		subtitle: t('resume.chosenOptions.subtitle'),
		iconItem: ['fal', 'check-circle'],
	}));
	const insuranceInfos = computed(() => ({
		icon: ['fal', 'shield-check'],
		title: t('resume.insurance.title'),
		iconItem: ['fal', 'circle-check'],
		subtitle: t(`resume.insurance.type.${currentOffer.value?.assurance_type}`),
	}));
	const optionsInfos = computed(() => ({
		icon: ['fal', 'plus-circle'],
		title: t('resume.options.title'),
		subtitle: t('resume.options.subtitle'),
		iconItem: ['fal', 'circle-xmark'],
		iconItemChosen: ['fal', 'circle-check'],
	}));

	const normalizedLoadingDemand = computed(() => ({
		dep_date1: demand.value?.dep_date1 ?? '',
		dep_date2: demand.value?.dep_date2 ?? '',
		final_dep_date_from: demand.value?.final_dep_date_from ?? '',
		final_dep_date_to: demand.value?.final_dep_date_to ?? '',
	}));
	const normalizedDeliveryDemand = computed(() => ({
		arr_date1: demand.value?.arr_date1 ?? '',
		arr_date2: demand.value?.arr_date2 ?? '',
		final_arr_date_from: demand.value?.final_arr_date_from ?? '',
		final_arr_date_to: demand.value?.final_arr_date_to ?? '',
	}));

	const minarmRightsLoader = computed(() => MinarmRights);
	const clientLoadDate = computed(() => demandHelper.formatDates(demand.value?.dep_date1 || '', demand.value?.dep_date2 || ''));
	const finalLoadDate = computed(() => demandHelper.formatFinalLoadingDates(normalizedLoadingDemand.value, currentOffer.value || undefined));
	const clientDeliveryDate = computed(() => demandHelper.formatDates(demand.value?.arr_date1 || '', demand.value?.arr_date2 || ''));
	const finalDeliveryDate = computed(() => demandHelper.formatFinalDeliveryDates(normalizedDeliveryDemand.value, currentOffer.value || undefined));

	const formule = computed(() => selectedFormule.value && {
		...formuleInfos.value,
		items: selectedFormule.value?.details,
	});

	const isInitialStatus = computed(() => !!(currentOffer.value && offersHelper.isInitialStatus(currentOffer.value)));
	const isInitialState = computed(() => offers.value && offersHelper.isInitialState(offers.value, projectSelected.value?.params?.prebooked));

	const leftToDo = computed(() => selectedFormule.value?.left_to_do && {
		...leftToDoInfos.value,
		items: selectedFormule.value?.left_to_do,
	});
	const services = computed(() => demand.value?.services?.services && {
		...servicesInfos.value,
		items: demand.value?.services?.services,
	});
	const chosenOptions = computed(() => demand.value?.services?.chosen_options && {
		...chosenOptionsInfos.value,
		items: demand.value?.services?.chosen_options,
	});
	const options = computed(() => demand.value?.services?.options && {
		...optionsInfos.value,
		items: demand.value?.services?.options,
	});

	const isRegisteredOnStripe = computed(() => !!projectSelected.value?.stripe_registered);

	return {
		disabledOffers,
		isMinarm,
		isCommissionaire,
		isIndividual,
		isValidationDoneByPartner,
		validOffer,
		isCBPaymentEnabled,
		leftToDoInfos,
		formuleInfos,
		isFinished,
		isFinishedStatus,
		isSigned,
		isValidForPcd,
		isBookingCharge,
		servicesInfos,
		chosenOptionsInfos,
		insuranceInfos,
		optionsInfos,
		minarmRightsLoader,
		clientLoadDate,
		finalLoadDate,
		clientDeliveryDate,
		finalDeliveryDate,
		formule,
		isInitialStatus,
		isInitialState,
		leftToDo,
		services,
		chosenOptions,
		options,
		isRegisteredOnStripe,
	};
}

